/*!

=========================================================
* Light Bootstrap Dashboard React - v2.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/light-bootstrap-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/light-bootstrap-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useState, useEffect } from "react";
import { useLocation, NavLink } from "react-router-dom";
import { Nav } from "react-bootstrap";
import { connect } from "react-redux";
import { getTranslate, LANGUAGE_KEYS } from "../../utils";
import { hasAccess } from "../../routes";
import logo from "assets/img/reactlogo.png";
import { IconUser } from "@tabler/icons-react";

// Helper function to render icon components
const renderIcon = (IconComponent, props = {}) => {
  if (!IconComponent) return null;
  // Increase size and adjust strokeWidth if desired
  const defaultProps = { size: 24, strokeWidth: 1.5 }; 
  return <IconComponent {...defaultProps} {...props} />;
};

function getGreeting(hour) {
  if (hour >= 5 && hour < 12) {
    return { text: 'Chào buổi sáng', icon: '☀️' };
  } else if (hour >= 12 && hour < 18) {
    return { text: 'Chào buổi chiều', icon: '🌤️' };
  } else if (hour >= 18 && hour < 23) {
    return { text: 'Chào buổi tối', icon: '🌙' };
  } else {
    return { text: 'Chúc ngủ ngon', icon: '😴' };
  }
}

function Sidebar({ color, image, routes, user }) {
  const location = useLocation();
  const [openItems, setOpenItems] = useState({});
  const userGroups = user?.groups || [];

  // Time state for greeting
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 60000); // update every minute
    return () => clearInterval(interval);
  }, []);

  const hour = now.getHours();
  const minute = now.getMinutes();
  const greeting = getGreeting(hour);
  const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

  const activeRoute = (routeName) => {
    return location.pathname === routeName ? "active" : "";
  };

  const toggleSubmenu = (path) => {
    if (!path) return;
    setOpenItems(currentOpenItems => ({
      ...currentOpenItems,
      [path]: !currentOpenItems[path]
    }));
  };

  return (
    <div className="sidebar" data-image={image} data-color={color}>
      <div
        className="sidebar-background"
        style={{
          backgroundImage: "url(" + image + ")"
        }}
      />
      <div className="sidebar-wrapper">
        <div className="logo d-flex align-items-center justify-content-start">
          <a
            href="https://autoecom.ai"
            className="simple-text logo-mini mx-1"
          >
            <div className="logo-img">
              <img src={require("assets/img/reactlogo.png")} alt="..." />
            </div>
          </a>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <a className="simple-text" href="http://autoecom.ai" style={{ textDecoration: 'none' }}>
              {getTranslate(LANGUAGE_KEYS.TEXT_TITLE)}
            </a>
            <span className="ml-2 badge bg-primary">v0.1</span>
            <span
              style={{ marginLeft: 10, fontSize: 20, cursor: 'pointer', verticalAlign: 'middle' }}
              title={window.localStorage.getItem('eit_language') === 'EN' ? 'Switch to Vietnamese' : 'Chuyển sang tiếng Anh'}
              onClick={e => {
                e.preventDefault();
                const current = window.localStorage.getItem('eit_language') === 'EN' ? 'VN' : 'EN';
                window.localStorage.setItem('eit_language', current);
                window.location.reload();
              }}
            >
              {window.localStorage.getItem('eit_language') === 'EN' ? '🇬🇧' : '🇻🇳'}
            </span> 
          </div>
        </div>
        <Nav>
          {routes.filter(route => !route.hidden && hasAccess(route, userGroups) && route.path !== '/my-account').map((prop, key) => {
            if (!prop.redirect) {
              if (prop.children && prop.children.length > 0) {
                const isOpen = !!openItems[prop.path];
                const filteredChildren = prop.children.filter(child => !child.hidden);

                if (filteredChildren.length === 0) return null;

                return (
                  <li
                    className={`${prop.topLine ? "top-line" : ""}`}
                    key={key}
                  >
                    <a
                      href="#"
                      className={`nav-link ${isOpen ? "" : "collapsed"}`}
                      onClick={(e) => {
                        e.preventDefault();
                        toggleSubmenu(prop.path);
                      }}
                      aria-expanded={isOpen}
                    >
                      <span className="sidebar-link-content">
                        {renderIcon(prop.icon, { className: 'sidebar-icon' })}
                        <p>
                          {prop.name}
                          <b className={`caret ${isOpen ? "caret-open" : ""}`}></b>
                        </p>
                      </span>
                    </a>
                    {isOpen && (
                      <ul className="nav nav-child mt-0">
                        {filteredChildren.map((child, childKey) => (
                          <li key={childKey}>
                            <NavLink
                              to={child.layout + child.path}
                              className="nav-link"
                              isActive={(_, location) => {
                                const targetUrl = child.layout + child.path;
                                return (location.pathname + location.search) === targetUrl;
                              }}
                              activeClassName="active"
                            >
                              <span className="sidebar-link-content">
                                {renderIcon(child.icon, { className: 'sidebar-sub-icon', size: 20 })}
                                <span className="sub-item-text">{child.name}</span>
                              </span>
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              } else {
                return (
                  <li
                    className={
                      prop.upgrade
                        ? `active active-pro ${prop.topLine ? "top-line" : ""}`
                        : activeRoute(prop.layout + prop.path) + (prop.topLine ? " top-line" : "")
                    }
                    key={key}
                  >
                    <NavLink
                      to={prop.layout + prop.path}
                      className={`nav-link`}
                      activeClassName="active"
                    >
                      <span className="sidebar-link-content">
                        {renderIcon(prop.icon, { className: 'sidebar-icon' })}
                        <p>{prop.name}</p>
                      </span>
                    </NavLink>
                  </li>
                );
              }
            }
            return null;
          })}
        </Nav>
        {/* <div className="sidebar-footer">
          <NavLink
            to="/v1/my-account"
            className="nav-link"
            activeClassName="active"
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '15px'
            }}
          >
            <span className="sidebar-link-content sidebar-greeting" style={{ width: '100%' }}>
              <div style={{ 
                textAlign: 'center',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                padding: '12px',
                width: '100%'
              }}>
                <div className="sidebar-greeting-label-row" style={{ marginBottom: '8px' }}>
                  <span className="sidebar-greeting-label" style={{ fontSize: '14px' }}>
                    {greeting.text} {greeting.icon}
                  </span>
                  <span className="sidebar-greeting-time" style={{ display: 'block', marginTop: '4px' }}>
                    <b>{timeStr}</b>
                  </span>
                </div>
                <span className="sidebar-greeting-name-row" style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}>
                  {renderIcon(IconUser, { className: 'sidebar-account-icon', size: 20 })}
                  <span className="sidebar-greeting-name" style={{ fontSize: '15px' }}>
                    {user?.full_name || user?.username}
                  </span>               
                </span>
              </div>
            </span>
          </NavLink>
        </div> */}
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  user: state.authenReducer.user
});

export default connect(mapStateToProps)(Sidebar);
